/* Sleep All Nite — behaviour the Turbo theme doesn't provide.
   Loaded after the theme's own scripts. */
(function () {
  'use strict';

  /* ------------------------------------------------------------------
     Collection filter drawer

     On phones the design replaces the sidebar with a Filter button that
     slides the same sidebar in from the left. Above that breakpoint the
     sidebar is a normal column and none of this runs — the CSS hides the
     button, and an open drawer left over from a resize is closed here so
     the body never keeps a stale scroll lock.
  ------------------------------------------------------------------ */
  var filterDrawer = {
    init: function () {
      var drawer = document.getElementById('sn-filter-drawer');
      var openButton = document.querySelector('[data-sn-filter-open]');

      if (!drawer || !openButton) return;

      var overlay = document.querySelector('[data-sn-drawer-overlay]');

      if (!overlay) {
        overlay = document.createElement('div');
        overlay.className = 'sn-drawer-overlay';
        overlay.setAttribute('data-sn-drawer-overlay', '');
        overlay.hidden = true;
        document.body.appendChild(overlay);
      }

      var isOpen = function () {
        return document.body.classList.contains('sn-filter-drawer-open');
      };

      var open = function () {
        overlay.hidden = false;
        document.body.classList.add('sn-filter-drawer-open');
        openButton.setAttribute('aria-expanded', 'true');
        // Focus the panel itself rather than the first control, so a
        // screen reader announces what just opened.
        drawer.setAttribute('tabindex', '-1');
        drawer.focus();
      };

      var close = function () {
        if (!isOpen()) return;
        document.body.classList.remove('sn-filter-drawer-open');
        openButton.setAttribute('aria-expanded', 'false');
        openButton.focus();
        // Wait out the slide before hiding the overlay, or it disappears
        // from under the panel mid-transition.
        window.setTimeout(function () {
          if (!isOpen()) overlay.hidden = true;
        }, 300);
      };

      openButton.addEventListener('click', function () {
        isOpen() ? close() : open();
      });

      overlay.addEventListener('click', close);

      document.addEventListener('click', function (event) {
        if (event.target.closest('[data-sn-filter-close]')) close();
      });

      document.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') close();
      });

      // A drawer left open across a resize would be stranded behind the
      // desktop layout, holding the scroll lock with nothing to close it.
      window.addEventListener('resize', function () {
        if (window.innerWidth >= 799) close();
      });
    },
  };

  /* ------------------------------------------------------------------
     Banner clearance

     The header is taken out of flow, so a template banner starts near the
     top of the page and the header sits over its first stretch. That is
     fine behind an image, but our header is solid navy, so on the plain
     night bands it simply covers the top of the copy — the blog's eyebrow
     ended up flush against the nav with all the slack below the subtitle.

     The overlap is published as a custom property and the banners pad
     themselves by it, so the copy centres in the part of the band you can
     actually see. It is measured rather than hard-coded because the header
     changes height: the promo bar can be dismissed, and it wraps on narrow
     screens.
  ------------------------------------------------------------------ */
  var bannerClearance = {
    init: function () {
      // Which element carries the header's height depends on the width: on
      // a phone the section itself is in flow and tall, while .header
      // collapses; on the desktop it is the other way round, because the
      // header is lifted out of flow and the section keeps no height. The
      // taller of the two is the one that is actually drawn.
      var section = document.getElementById('shopify-section-header');
      var header = document.querySelector('.header');
      // Every band the header is drawn over: the plain template banners and
      // the home page's hero, which is the same problem in a different
      // section.
      var banner = document.querySelector(
        '.page-banner--plain, body.index .shopify-section--image-with-text-overlay .banner'
      );

      if (!banner || (!section && !header)) return;

      var apply = function () {
        var drawnHeight = Math.max(
          section ? section.offsetHeight : 0,
          header ? header.offsetHeight : 0
        );
        // Measured against the document, so the result does not change as
        // the page scrolls. A header that is in flow starts at or above the
        // banner's own top and the difference goes negative, which is the
        // clamp's way of saying nothing is covered.
        var bannerTop = banner.getBoundingClientRect().top + window.pageYOffset;
        var overlap = Math.max(0, Math.round(drawnHeight - bannerTop));

        document.documentElement.style.setProperty('--sn-banner-overlap', overlap + 'px');
      };

      apply();
      window.addEventListener('resize', apply);

      if (window.ResizeObserver) {
        var observer = new ResizeObserver(apply);
        if (section) observer.observe(section);
        if (header) observer.observe(header);
      }
    },
  };

  /* A <details> does not open when it is the :target, so a link like
     /pages/faq#how-do-returns-work would land on a closed row. Open it and
     bring it under the fixed header. */
  var faqAnchor = {
    row: function () {
      var hash = window.location.hash;
      if (!hash || hash.length < 2) return null;

      var row;
      try {
        row = document.querySelector(hash);
      } catch (e) {
        return null;
      }
      return row && row.classList.contains('sn-faq__row') ? row : null;
    },

    /* The header is fixed, so the row has to clear it. Measured after the
       row is open and after `load`, because web fonts and the announcement
       bar both change the document height under a scroll taken earlier. */
    reveal: function () {
      var row = faqAnchor.row();
      if (!row) return;

      row.open = true;

      var header = document.querySelector('.header');
      var offset = (header ? header.offsetHeight : 0) + 16;
      var top = row.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top: Math.max(0, top), behavior: 'auto' });
    },

    init: function () {
      if (!document.querySelector('.sn-faq__row')) return;

      faqAnchor.reveal();
      window.addEventListener('hashchange', faqAnchor.reveal);

      /* The browser runs its own :target scroll once the document is done,
         and that lands after any pass scheduled with rAF alone — it puts
         the row under the fixed header. A short timeout runs last. */
      var settle = function () {
        requestAnimationFrame(faqAnchor.reveal);
        setTimeout(faqAnchor.reveal, 120);
      };

      if (document.readyState === 'complete') {
        settle();
      } else {
        window.addEventListener('load', settle);
      }
    },
  };

  /* ------------------------------------------------------------------
     Reviews anchor

     The star line under the product title points at the Judge.me widget
     lower down the page. The widget is an app block that arrives with the
     app's own script, so at render time nothing in Liquid can name the
     band it lands in — the link's target is put on it here.

     Naming it is not decoration. InstantClick handles a fragment that
     resolves to nothing by sending the page to the top, which is where
     the link went while its target did not exist yet.

     The scroll itself is taken rather than left to the browser for the
     same reason the FAQ rows take theirs: the header is fixed, and a
     fragment jump leaves the heading under it.
  ------------------------------------------------------------------ */
  var reviewsAnchor = {
    id: 'sn-reviews',

    /* The band, not the widget: the widget's script rewrites its own
       container, so an id put on that would not survive. */
    band: function () {
      var widget = document.querySelector('.jdgm-widget, .jm-review-widget');
      if (!widget) return null;

      return (
        widget.closest('.product-app--container') ||
        widget.closest('.shopify-section') ||
        widget
      );
    },

    name: function () {
      var band = reviewsAnchor.band();
      if (band && !band.id) band.id = reviewsAnchor.id;
      return band;
    },

    init: function () {
      var links = document.querySelectorAll('[data-sn-reviews-link]');
      if (!links.length) return;

      /* The widget is usually still on its way at DOMContentLoaded, so the
         band is named again on each click. */
      reviewsAnchor.name();

      Array.prototype.forEach.call(links, function (link) {
        link.addEventListener('click', function (event) {
          var band = reviewsAnchor.name();
          if (!band) return;

          event.preventDefault();
          event.stopPropagation();

          var header = document.querySelector('.header');
          var offset = (header ? header.offsetHeight : 0) + 16;
          var top = band.getBoundingClientRect().top + window.pageYOffset - offset;

          /* `auto`, like the FAQ rows: a smooth scroll asked for on a page
             the browser is still settling is dropped outright, and this
             link is at the top of the page where that is most likely. */
          window.scrollTo({ top: Math.max(0, top), behavior: 'auto' });
        });
      });
    },
  };

  /* ------------------------------------------------------------------
     Blog category bar

     Tags are the blog's categories, so the bar grows with every article
     and a few posts in it is deep enough to push the first card off the
     screen. Collapsed, it shows one row and a More button counting what
     is left; expanded, the rows wrap as they always did.

     The phone layout scrolls the pills sideways instead (see the CSS), so
     above that breakpoint only. Nothing here runs without the button, so
     with JS off every pill stays visible.
  ------------------------------------------------------------------ */
  var blogFilters = {
    init: function () {
      var pills = document.querySelector('[data-sn-blog-pills]');
      var button = document.querySelector('[data-sn-blog-more]');

      if (!pills || !button) return;

      var label = button.querySelector('[data-sn-blog-more-label]');
      var expanded = false;

      var hidden = function () {
        /* Measured against the first pill rather than a fixed height, so a
           longer label or a different font size can't clip a row. */
        var items = pills.children;
        var first = items[0];

        if (!first) return { count: 0, row: 0 };

        var top = first.offsetTop;
        var count = 0;

        for (var i = 1; i < items.length; i++) {
          if (items[i].offsetTop > top + 1) count++;
        }

        return { count: count, row: first.offsetHeight };
      };

      var apply = function () {
        if (window.innerWidth <= 798) {
          pills.style.maxHeight = '';
          button.hidden = true;
          return;
        }

        /* Measure with the pills unclamped and the button out of the way,
           then again with the button in place: it sits in the first row and
           takes width from it, which can push one more pill over. */
        pills.style.maxHeight = '';
        button.hidden = true;

        if (hidden().count === 0) return;

        button.hidden = false;

        var overflow = hidden();

        if (overflow.count === 0) {
          /* Only the button's own width was pushing a pill over; the row
             fits without it. */
          button.hidden = true;
          return;
        }

        if (expanded) {
          label.textContent = 'Fewer';
        } else {
          pills.style.maxHeight = overflow.row + 'px';
          label.textContent = 'More (' + overflow.count + ')';
        }

        button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      };

      button.addEventListener('click', function () {
        expanded = !expanded;
        apply();
      });

      var queued = false;
      window.addEventListener('resize', function () {
        if (queued) return;
        queued = true;
        requestAnimationFrame(function () {
          queued = false;
          apply();
        });
      });

      apply();

      /* Pill widths move as the font loads, which changes what fits. */
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(apply);
      }
    },
  };

  /* ------------------------------------------------------------------
     Newsletter popup: the success state

     The signup form posts and the page comes back at ?customer_posted=true
     with the thank-you and the welcome code rendered inside the popup. By
     then Turbo has set its 30-day 'popup' cookie, so window.newsletter_popup
     will not open again and nobody ever reads it.

     Reopening it here is deliberately narrow: only when this visitor
     submitted the popup's own form, which the flag below records as they
     submit. Sign up in the footer and that form shows its own confirmation
     inline, with no modal on top of it.
  ------------------------------------------------------------------ */
  var newsletterPopup = {
    key: 'sn-newsletter-popup-submit',

    init: function () {
      var popup = document.querySelector('.js-newsletter-popup');

      if (!popup) return;

      var form = popup.querySelector('form');

      if (form) {
        form.addEventListener('submit', function () {
          try {
            window.sessionStorage.setItem(newsletterPopup.key, '1');
          } catch (e) {
            /* Private mode, or storage turned off: the confirmation still
               renders in the popup, it just won't be reopened for them. */
          }
        });
      }

      if (!popup.querySelector('.sn-newsletter-success')) return;

      var submitted = false;

      try {
        submitted = window.sessionStorage.getItem(newsletterPopup.key) === '1';
        window.sessionStorage.removeItem(newsletterPopup.key);
      } catch (e) {
        return;
      }

      if (!submitted) return;

      var $ = window.jQuery;

      if (!$ || !$.fancybox) return;

      /* Same lightbox options Turbo opens the popup with, minus the timer:
         they asked for this one, so it should not keep them waiting. */
      $.fancybox.open($(popup), {
        baseClass: 'newsletter__lightbox',
        hash: false,
        infobar: false,
        toolbar: false,
        loop: true,
        smallBtn: true,
        mobile: {
          preventCaptionOverlap: false,
          toolbar: true,
          buttons: ['close'],
        },
      });
    },
  };

  /* ------------------------------------------------------------------
     Copy the welcome code

     The button ships hidden and is shown here, so a visitor without JS is
     never offered a control that cannot work — the code itself is text on
     the page either way.
  ------------------------------------------------------------------ */
  var copyCode = {
    /* execCommand is deprecated but still the only path when the Clipboard
       API is missing (older Safari) or refused (permissions policy, an
       unfocused document). Returns whether the copy actually happened. */
    legacy: function (text) {
      var field = document.createElement('textarea');
      var copied = false;

      field.value = text;
      field.setAttribute('readonly', '');
      field.style.position = 'fixed';
      field.style.top = '0';
      field.style.opacity = '0';
      document.body.appendChild(field);
      field.select();

      try {
        copied = document.execCommand('copy');
      } catch (e) {
        copied = false;
      }

      document.body.removeChild(field);

      return copied;
    },

    init: function () {
      var buttons = document.querySelectorAll('[data-sn-copy-code]');

      if (!buttons.length) return;

      Array.prototype.forEach.call(buttons, function (button) {
        var code = button.getAttribute('data-sn-copy-code');
        var original = button.textContent.trim();

        var done = function () {
          button.textContent = button.getAttribute('data-sn-copy-done');
          button.classList.add('is-copied');

          setTimeout(function () {
            button.textContent = original;
            button.classList.remove('is-copied');
          }, 2000);
        };

        button.hidden = false;

        button.addEventListener('click', function () {
          if (navigator.clipboard) {
            navigator.clipboard.writeText(code).then(done, function () {
              /* Refused: fall back before giving up, and if that fails too
                 say nothing — the code is on screen to be read. */
              if (copyCode.legacy(code)) done();
            });
            return;
          }

          if (copyCode.legacy(code)) done();
        });
      });
    },
  };

  /* ------------------------------------------------------------------
     Sleep quiz (sections/sn-quiz.liquid)

     One question at a time, then score. Each answer carries its weights
     as "tag=weight, tag=weight"; a product earns every weight whose tag
     it carries, and "tag=!" drops products without the tag. The pick is
     the top scorer, ties going to the earlier product in the section's
     list, and is never a bundle. The pair is whatever best covers the
     needs (weight 2+) the pick doesn't, so someone who also asked about
     skin sees the night cream, and someone who only asked about sleep
     sees one product.

     Answers go in the hash (#answers=0.2.1.1.0.2) so a result survives a
     reload and can be sent to someone.
  ------------------------------------------------------------------ */
  /* ------------------------------------------------------------------
     Cart badge

     The header renders the count bubble on every page, hidden at zero, so
     an ajax add from an empty cart has an element to update — Turbo's
     refreshCart only rewrites an existing .cart_count. It writes the new
     number but knows nothing about the empty state, so this follows it.
  ------------------------------------------------------------------ */
  var cartBadge = {
    sync: function (badge) {
      var count = parseInt(badge.textContent, 10) || 0;
      badge.classList.toggle('is-empty', count === 0);
    },

    init: function () {
      var badges = document.querySelectorAll('.cart_count');
      if (!badges.length || !window.MutationObserver) return;

      Array.prototype.forEach.call(badges, function (badge) {
        new MutationObserver(function () {
          cartBadge.sync(badge);
        }).observe(badge, { childList: true, characterData: true, subtree: true });
      });
    },
  };

  var QUIZ_NEED = 2;

  var quiz = {
    parse: function (raw) {
      var weights = [];

      (raw || '').split(',').forEach(function (pair) {
        var at = pair.lastIndexOf('=');
        if (at < 1) return;

        var tag = pair.slice(0, at).trim().toLowerCase();
        var value = pair.slice(at + 1).trim();

        weights.push({
          tag: tag,
          required: value === '!',
          weight: value === '!' ? 0 : parseFloat(value) || 0,
        });
      });

      return weights;
    },

    score: function (products, weights) {
      var required = weights.filter(function (w) { return w.required; });
      var eligible = products.filter(function (p) {
        return required.every(function (w) { return p.tags.indexOf(w.tag) !== -1; });
      });
      var relaxed = required.length > 0 && eligible.length === 0;

      if (relaxed) eligible = products.slice();

      /* A weight of 2 or more is a need; 1 is a preference. Preferences
         only count for a product that meets a need, or a format answer
         alone would send someone who can't fall asleep to a hangover
         strip. */
      var needs = weights.filter(function (w) { return !w.required && w.weight >= QUIZ_NEED; });

      eligible.forEach(function (p) {
        var fits = needs.some(function (w) { return p.tags.indexOf(w.tag) !== -1; });

        p.score = 0;
        if (!fits) return;

        weights.forEach(function (w) {
          if (!w.required && p.tags.indexOf(w.tag) !== -1) p.score += w.weight;
        });
      });

      if (!eligible.some(function (p) { return p.score > 0; })) relaxed = true;

      var best = function (list, key) {
        return list.reduce(function (top, p) {
          if (!top) return p;
          if (key(p) > key(top)) return p;
          if (key(p) === key(top) && p.score > top.score) return p;
          return top;
        }, null);
      };

      /* A bundle is never the one product; it can be the pair. */
      var singles = eligible.filter(function (p) { return p.tags.indexOf('format:bundle') === -1; });
      var pick = best(singles.length ? singles : eligible, function (p) { return p.score; });
      if (!pick) return null;

      /* The needs the pick leaves uncovered, per remaining product. */
      var rest = eligible.filter(function (p) { return p !== pick; });
      rest.forEach(function (p) {
        p.gap = 0;
        needs.forEach(function (w) {
          if (p.tags.indexOf(w.tag) !== -1 && pick.tags.indexOf(w.tag) === -1) {
            p.gap += w.weight;
          }
        });
      });

      var pair = best(rest, function (p) { return p.gap; });
      if (pair && pair.gap <= 0) pair = null;

      return { pick: pick, pair: pair, relaxed: relaxed };
    },

    init: function () {
      var root = document.querySelector('[data-sn-quiz]');
      if (!root) return;

      var form = root.querySelector('[data-sn-quiz-form]');
      var steps = Array.prototype.slice.call(root.querySelectorAll('[data-sn-quiz-step]'));
      var next = root.querySelector('[data-sn-quiz-next]');
      var back = root.querySelector('[data-sn-quiz-back]');
      var current = root.querySelector('[data-sn-quiz-current]');
      var bar = root.querySelector('[data-sn-quiz-bar]');
      var result = root.querySelector('[data-sn-quiz-result]');
      var pickSlot = root.querySelector('[data-sn-quiz-pick]');
      var pairSlot = root.querySelector('[data-sn-quiz-pair]');
      var pairWrap = root.querySelector('[data-sn-quiz-pair-wrap]');
      var note = root.querySelector('[data-sn-quiz-note]');
      var pool = root.querySelector('.sn-quiz__pool');
      var card = root.querySelector('.sn-quiz__card');
      var index = 0;

      if (!steps.length) return;

      var products = Array.prototype.map.call(
        root.querySelectorAll('[data-sn-quiz-product]'),
        function (el) {
          return {
            el: el,
            tags: (el.getAttribute('data-tags') || '').toLowerCase().split('||'),
          };
        }
      );

      root.classList.add('is-ready');
      root.querySelector('[data-sn-quiz-progress]').hidden = false;
      root.querySelector('[data-sn-quiz-nav]').hidden = false;
      root.querySelector('[data-sn-quiz-fallback]').hidden = true;

      /* Bring the card's top edge back into view below the fixed header
         when a long answer list left it scrolled off. Only ever upward: a
         card already in view stays put. The section wrapper, not .header,
         is what is fixed at every width; .header measures 0 on phones. */
      var reveal = function () {
        var header = document.querySelector('.shopify-section--header');
        var offset = (header ? header.offsetHeight : 0) + 16;
        var top = card.getBoundingClientRect().top;
        if (top >= offset) return;
        var still = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        window.scrollTo({
          top: Math.max(0, top + window.pageYOffset - offset),
          behavior: still ? 'auto' : 'smooth',
        });
      };

      var chosen = function (step) {
        return step.querySelector('input[type="radio"]:checked');
      };

      var answers = function () {
        return steps.map(function (step) {
          var input = chosen(step);
          return input ? input.value : '';
        });
      };

      var show = function (i, focus) {
        index = i;
        steps.forEach(function (step, n) {
          step.classList.toggle('is-active', n === i);
        });

        var last = i === steps.length - 1;
        next.textContent = next.getAttribute(last ? 'data-label-finish' : 'data-label-next');
        next.disabled = !chosen(steps[i]);
        back.hidden = i === 0;
        current.textContent = i + 1;
        bar.style.width = ((i + 1) / steps.length) * 100 + '%';

        if (focus) {
          steps[i].querySelector('legend').focus({ preventScroll: true });
          reveal();
        }
      };

      var reset = function () {
        Array.prototype.forEach.call(
          root.querySelectorAll('[data-sn-quiz-product]'),
          function (el) { pool.appendChild(el); }
        );
      };

      var finish = function (focus) {
        var weights = [];

        steps.forEach(function (step) {
          var input = chosen(step);
          if (input) weights = weights.concat(quiz.parse(input.getAttribute('data-weights')));
        });

        var match = quiz.score(products, weights);
        if (!match) return;

        reset();
        pickSlot.appendChild(match.pick.el);
        pairWrap.hidden = !match.pair;
        if (match.pair) pairSlot.appendChild(match.pair.el);
        note.hidden = !match.relaxed;

        form.hidden = true;
        result.hidden = false;

        if (history.replaceState) {
          history.replaceState(null, '', '#answers=' + answers().join('.'));
        }

        if (focus) {
          result.focus({ preventScroll: true });
          reveal();
        }
      };

      form.addEventListener('change', function (event) {
        if (event.target.type === 'radio') next.disabled = false;
      });

      form.addEventListener('submit', function (event) {
        event.preventDefault();
        if (!chosen(steps[index])) return;

        if (index < steps.length - 1) {
          show(index + 1, true);
        } else {
          finish(true);
        }
      });

      back.addEventListener('click', function () {
        if (index > 0) show(index - 1, true);
      });

      root.querySelector('[data-sn-quiz-retake]').addEventListener('click', function () {
        form.reset();
        reset();
        result.hidden = true;
        form.hidden = false;
        if (history.replaceState) {
          history.replaceState(null, '', location.pathname + location.search);
        }
        show(0, true);
      });

      /* Restore a shared or reloaded result. */
      var saved = /[#&]answers=([\d.]+)/.exec(location.hash);
      if (saved) {
        var values = saved[1].split('.');
        var complete = steps.every(function (step, n) {
          var input = step.querySelector('input[value="' + values[n] + '"]');
          if (input) input.checked = true;
          return !!input;
        });

        if (complete) {
          show(steps.length - 1, false);
          finish(false);
          return;
        }

        form.reset();
      }

      show(0, false);
    },
  };

  /* ------------------------------------------------------------------
     Product thumbnail row

     The CSS lays the thumbnails out as a row that scrolls natively, but
     Turbo still runs them through Flickity, which grabbed a sideways
     swipe on an iPhone and tried to slide cells the CSS holds still — so
     the row did not move at all. Turning off Flickity's dragging leaves
     the swipe to the browser. Flickity's asNavFor selected on tap through
     its drag handlers, so taps are handled here instead. Swiping the main
     image brings its thumbnail into view, since the row no longer slides
     itself.

     Turbo's flickityIosFix (utilities.js) cancels any sideways touchmove
     that starts inside a .flickity-slider, listening on <body>. The row is
     still one, so its touches stop here and never reach that listener.
  ------------------------------------------------------------------ */
  var thumbnailRow = {
    init: function () {
      if (!window.Flickity) return;

      document.querySelectorAll('.js-product-section').forEach(function (section) {
        var nav = section.querySelector('.product_gallery_nav');
        var main = section.querySelector('.js-product-gallery');

        if (!nav || !main) return;

        var keepTouch = function (event) {
          event.stopPropagation();
        };
        nav.addEventListener('touchstart', keepTouch, { passive: true });
        nav.addEventListener('touchmove', keepTouch, { passive: true });

        var attach = function () {
          var thumbs = window.Flickity.data(nav);
          var gallery = window.Flickity.data(main);

          if (!thumbs || !gallery) return false;

          thumbs.options.draggable = false;
          thumbs.updateDraggable();

          nav.addEventListener('click', function (event) {
            var cell = event.target.closest('.gallery-cell');
            if (!cell) return;
            var index = Array.prototype.indexOf.call(nav.querySelectorAll('.gallery-cell'), cell);
            if (index > -1) gallery.select(index);
          });

          gallery.on('select', function () {
            var viewport = thumbs.viewport;
            var cell = nav.querySelectorAll('.gallery-cell')[gallery.selectedIndex];
            if (!cell) return;

            var box = viewport.getBoundingClientRect();
            var rect = cell.getBoundingClientRect();
            if (rect.left >= box.left && rect.right <= box.right) return;

            viewport.scrollTo({
              left: viewport.scrollLeft + rect.left - box.left - (box.width - rect.width) / 2,
              behavior: 'smooth',
            });
          });

          return true;
        };

        // Turbo builds the galleries in its own deferred script; if they
        // are not there yet, they are by load.
        if (!attach()) window.addEventListener('load', attach, { once: true });
      });
    },
  };

  var start = function () {
    thumbnailRow.init();
    copyCode.init();
    cartBadge.init();
    quiz.init();
    filterDrawer.init();
    bannerClearance.init();
    faqAnchor.init();
    reviewsAnchor.init();
    blogFilters.init();
    newsletterPopup.init();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
